import { inject, observer } from 'mobx-react';
import * as React from 'react';
import { RouteComponentProps } from 'react-router-dom';
import { Button, Card, ErrorMessage, FlexRow, Footer, FormCell, Grid, PageLoading, WorkflowHeader } from 'ui-toolkit';
import { ICommitmentException } from '../classes/ICommitmentException';
import { ISpecificException } from '../classes/ISpecificExceptions';
import { PolicyTypeEnum } from '../classes/PolicyTypeEnum';
import { ReqExcSections } from '../classes/ReqExcSections';
import PolicyHeaderButtons from '../components/PolicyHeaderButtons/PolicyHeaderButtons';
import { RequirementsExceptions } from '../components/RequirementsExceptions';
import IRouterParams from '../IRouterParams';
import { RootStore } from '../services/RootStore';
import getText from '../utils/getText';
import { clientUrls } from '../../ui-toolkit/rest/ClientUrlFactory';

interface IAppProps extends RouteComponentProps<IRouterParams> {
  rootStore: RootStore;
}

interface IPolicyScheduleBState {
  nextPage: string;
}

class PolicyScheduleB extends React.Component<IAppProps, IPolicyScheduleBState> {
  public static defaultProps: Partial<IAppProps> = {};

  public readonly state: IPolicyScheduleBState = {
    nextPage: '',
  };

  private orderID: string = this.props!.match.params.orderId;
  private policyType: PolicyTypeEnum = this.props!.match.params.policyType;

  constructor(props: IAppProps) {
    super(props);
    if (this.props.match) {
      this.props.rootStore.policyScheduleAService.initPolicyScheduleAData(this.policyType, this.orderID);
    }
    this.props.rootStore.policyScheduleBService.initExceptions(this.orderID, this.policyType);
  }

  public render() {
    return (
      <div className="App">
        <div>
          <WorkflowHeader
            title={getText(`policy.${this.props.match!.params.action}.${this.props.match!.params.policyType}`)}
            onBackClick={this.goBack}
            onHomeClick={this.goToHomePage}
          >
            <PolicyHeaderButtons
              showSaveSuccess={this.props.rootStore.policyScheduleBService.getSaveDataFetch.success}
              disableFinalize={this.disableGeneratePolicyButton}
              onNext={this.goNext}
            />
          </WorkflowHeader>
          <div className={'page-content'}>
            {this.errorMessage() === '' ? null : <ErrorMessage message={this.errorMessage()} />}
            <Card>
              <Grid>
                <FlexRow justifyContent={'space-between'}>
                  <FormCell label={'Step 2:'}>
                    <h2>Finish Schedule B</h2>
                  </FormCell>
                  <FlexRow>
                    <Button intent="secondary" onClick={this.onReset} className={'delete-button'}>
                      Reset
                    </Button>
                  </FlexRow>
                </FlexRow>
                <RequirementsExceptions
                  fetching={this.requirementsExceptionsFetching()}
                  addButtonText={'+ Add Exceptions'}
                  addCustomItem={this.addCustomItem}
                  section={ReqExcSections.B2}
                  onTextChanged={this.onTextChanged}
                  addNewItem={this.onAddItem}
                  onDeleteItem={this.onDeleteItem}
                  specificExceptions={this.getSpecificExceptions(ReqExcSections.B2)}
                  onOrderChanged={this.onOrderChanged}
                  requirementsExceptions={this.getExcList()}
                />
              </Grid>
            </Card>
          </div>
        </div>
        <Footer>
          <FlexRow justifyContent={'space-between'} width={100}>
            <FlexRow justifyContent={'start'}>
              <Button intent="primary" onClick={this.goPrev}>
                Back
              </Button>
              <span>Back: Schedule A</span>
            </FlexRow>
            <FlexRow justifyContent={'flex-end'}>
              <span>Next: Review & Finalize</span>
              <PolicyHeaderButtons
                showSaveSuccess={this.props.rootStore.policyScheduleBService.getSaveDataFetch.success}
                disableFinalize={this.disableGeneratePolicyButton}
                onNext={this.goNext}
              />
            </FlexRow>
          </FlexRow>
        </Footer>
        <PageLoading
          isLoading={
            this.requirementsExceptionsFetching() ||
            !!this.props.rootStore.policyScheduleBService.getSaveDataFetch.fetching
          }
        />
      </div>
    );
  }

  private goBack = () => {
    this.props.history.push(clientUrls.urlFor.title(this.orderID));
  };

  private getExcList = (): ICommitmentException[] => {
    return this.props.rootStore.policyScheduleBService.getExceptionsList();
  };

  private getSpecificExceptions = (sectionName: ReqExcSections): ISpecificException[] => {
    return this.props.rootStore.policyScheduleBService.getSpecificExceptions(sectionName);
  };

  private onDeleteItem = (sectionName: ReqExcSections, item: ICommitmentException) => {
    this.props.rootStore.policyScheduleBService.deleteItem(item, this.orderID, this.policyType);
  };

  private onAddItem = (sectionName: ReqExcSections, item: ISpecificException) => {
    this.props.rootStore.policyScheduleBService.addItem(sectionName, item, this.orderID, this.policyType);
  };

  private onOrderChanged = (sectionName: ReqExcSections, newArray: ICommitmentException[]) => {
    this.props.rootStore.policyScheduleBService.onOrderChanged(sectionName, newArray, this.orderID, this.policyType);
  };

  private goNext = async () => {
    this.props.rootStore.policyScheduleBService.setSaveDataFetchDefault();
    this.props.history.push({
      pathname: clientUrls.urlFor.policyReview(
        this.orderID,
        this.policyType,
        this.props.match.params.action as 'edit' | 'create'
      ),
    });
  };

  private errorMessage = (): string => {
    return this.props.rootStore.policyScheduleBService.getPageError;
  };

  private addCustomItem = (sectionName: ReqExcSections, text: string) => {
    this.props.rootStore.policyScheduleBService.addCustomItem(sectionName, text, this.orderID, this.policyType);
  };

  private onTextChanged = (sectionName: ReqExcSections, newText: string, item: ICommitmentException) => {
    this.props.rootStore.policyScheduleBService.setChangedText(
      sectionName,
      newText,
      item,
      this.orderID,
      this.policyType
    );
  };

  private onReset = () => {
    this.props.rootStore.policyScheduleBService.resetExceptions(
      this.props.match.params.orderId,
      this.props.match.params.policyType
    );
  };

  private requirementsExceptionsFetching = (): boolean => {
    return this.props.rootStore.policyScheduleBService.getLoadPage.fetching!;
  };

  private get disableGeneratePolicyButton(): boolean {
    return !this.props.rootStore.policyScheduleAService.getPolicyJacketIsValid;
  }

  private goToHomePage = () => {
    this.props.history.push(clientUrls.urlFor.adminOrders());
  };

  private goToScheduleA = () => {
    this.props.history.push({
      pathname: clientUrls.urlFor.policyGeneration(
        this.orderID,
        this.policyType,
        this.props.match.params.action as 'edit' | 'create'
      ),
    });
  };

  private goPrev = () => {
    this.props.rootStore.policyScheduleBService.setSaveDataFetchDefault();
    this.goToScheduleA();
  };
}

export default inject('rootStore')(observer(PolicyScheduleB));
